<template>
  <div class="early-settlement-left-content" :class="{ 'has-error': requestAmountErrorText }">
    <div class="early-settlement-left-content-title">
      <span class="early-settlement-left-content-title-text">Erken ödeme nedir?</span>
      <span class="early-settlement-left-content-description">
        Hak edişinizi almak için vade beklemenize gerek yok! Erken ödeme talep edin, kazancınız sadece
        <span class="early-settlement-left-content-description-bold">
          günlük %{{ summary?.percentageValue }}
        </span>
        hizmet komisyonuyla 
        <span class="early-settlement-left-content-description-bold">hemen ilk iş günü</span> 
        hesabınıza yatsın.
      </span>
    </div>
    <div class="early-settlement-left-content-info">
      <div class="early-settlement-left-content-info-container">
        <div class="item">
          <span class="item-label">Güncel Bakiye Tutarı</span>
          <span class="item-value">
            <template v-if="summary?.currentBalance != null">₺{{ summary.currentBalance }}</template>
          </span>
        </div>
        <div class="item">
          <span class="item-label">Gelecek Ödemeleriniz için Ayrılan Tutar</span>
          <span class="item-value">
            <template v-if="summary?.reservedForFuturePayments != null">
              {{ Number(summary.reservedForFuturePayments) !== 0 ? `- ₺${summary.reservedForFuturePayments}` : '₺0' }}
            </template>
          </span>
        </div>
        <div class="item">
          <span class="item-label">Onay Bekleyen İadeler için Ayrılan Tutar</span>
          <span class="item-value">
            <template v-if="summary?.reservedForPendingRefunds != null">
              {{ Number(summary.reservedForPendingRefunds) !== 0 ? `- ₺${summary.reservedForPendingRefunds}` : '₺0' }}
            </template>
          </span>
        </div>
        <div class="item">
          <div class="item-label-container">
            <span class="item-label">Teminat Tutarı</span>
            <TooltipPlugin tooltipText="Erken ödeme talep ettiğinizde, güncel bakiyenizin bir kısmı sonradan oluşabilecek iadeler, faturalar ve diğer giderleriniz için bakiyenizde tutulur. Bu teminat tutarı vade zamanı geldiğinde hesabınıza yatırılır.">
              <template #tooltip-btn>
                <Icons 
                class="tooltip-info-icon"
                name="InfoCircle" 
                color="#7382a0" 
                size="sm"
                />
              </template>
            </TooltipPlugin>
          </div>
          <span class="item-value">
            <template v-if="summary?.guaranteeAmount != null">
              {{ Number(summary.guaranteeAmount) !== 0 ? `- ₺${summary.guaranteeAmount}` : '₺0' }}
            </template>
          </span>
        </div>
        <div class="item">
          <div class="item-label-container-wide">
            <span class="item-label">Ek Kesintiler</span>
            <TooltipPlugin tooltipText="Bekleyen kargo, ceza, vergi ve reklam faturaları için ayrılan tutar.">
              <template #tooltip-btn>
                <Icons 
                class="tooltip-info-icon"
                name="InfoCircle" 
                color="#7382a0"
                size="sm"
                />
              </template>
            </TooltipPlugin>
          </div>
          <span class="item-value">
            <template v-if="summary?.extraDeductions != null">
              {{ Number(summary.extraDeductions) !== 0 ? `- ₺${summary.extraDeductions}` : '₺0' }}
            </template>
          </span>
        </div>
        <div class="item total-amount">
          <span class="item-label">Talep Edebileceğiniz En Yüksek Tutar</span>
          <span class="item-value">
            <template v-if="summary?.maxRequestableAmount != null">₺{{ summary.maxRequestableAmount }}</template>
          </span>
        </div>
      </div>
      <div class="info-calculation">
        <div class="info-calculation-input">
        <div class="info-calculation-input-field">
          <div class="info-calculation-input-field-app-button">
            <Input 
              ref="amountInputRef"
              id="max-request-amount" 
              type="text" 
              inputWidth="476px"
              size="lg"
              label="Erken Ödeme Talep Ettiğiniz Tutar"
              v-model:value="requestAmount"
              :isError="!!requestAmountErrorText"
              :disabled="isRequestCreated || !isCreationAllowed || availabilityLoading || isBelowMinRequestAmount || isMaxRequestableAmountZero || !gradeAvailably"
              @input="handleInput"
            >
              <template #right-icon>
                <span
                  class="use-all-btn"
                  :class="{ 'use-all-btn--disabled': isUseAllDisabled }"
                  @click="handleUseAllClick"
                >
                  Tamamını Kullan
                </span>
              </template>
            </Input>
            <p class="error-text" :class="{ 'error-text-visible': requestAmountErrorText }">{{ requestAmountErrorText }}</p>
          </div>
        <AppButton
          secondary
          lg
          title="Hesapla"
          :disabled="isRequestCreated || !requestAmount || !!requestAmountErrorText || !isCreationAllowed || availabilityLoading || isBelowMinRequestAmount || isMaxRequestableAmountZero || !gradeAvailably"
          @click="handleCalculateRequest"
          class="calculate-btn"
        />
        </div>
      </div>
      </div>
      <div class="early-settlement-left-content-info-additional-info">
        <span class="early-settlement-left-content-info-additional-info-description">
          Yukarıdaki bakiye detayları, en son
          <span class="early-settlement-left-content-info-additional-info-description-bold">
            {{ summary?.referenceDate }}
          </span>
          tarihli siparişler eklenerek hesaplanmıştır.
          <br />Veriler, her günün sonunda siparişler tamamlandıktan sonra güncellenir.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { Input, AppButton, TooltipPlugin, Icons } from '@n11/so-ui-components';
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { sendEarlySettlementCalculateEvent, sendEarlySettlementCalculateDismissEvent, sendUseAllBalanceEvent } from '@/utils/ga4/helper';
export default {
  name: "EarlySettlementLeftContent",
  components: {
    Input,
    AppButton,
    TooltipPlugin,
    Icons,
  },
  emits: ['calculate', 'validation-error'],
  setup(props, { emit }) {
    const requestAmount = ref(null);
    const requestAmountErrorText = ref('');
    const isNormalizing = ref(false);
    const amountInputRef = ref(null);

    const parseAmount = (value) => {
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return value;

      let strValue = String(value);
      strValue = strValue.replace(/₺/g, '').trim();
      strValue = strValue.replace(/\./g, '').replace(',', '.');

      return parseFloat(strValue) || 0;
    };

    const isMaxRequestableAmountZero = computed(() => {
      if (!props.summary) return false;

      const maxAmount = parseAmount(props.summary.maxRequestableAmount);
      return maxAmount === 0;
    });

    const isBelowMinRequestAmount = computed(() => {
      if (!props.summary) return false;

      const maxAmount = parseAmount(props.summary.maxRequestableAmount);
      const minAmount = parseAmount(props.summary.minRequestAmount);

      if (!minAmount) return false;

      return maxAmount > 0 && maxAmount < minAmount;
    });

    const gradeAvailably = computed(() => {
      return props.availability?.availabilityGradeDto?.gradeAvailably ?? false;
    });

    const getMaxAmount = () => {
      if (!props.summary || props.summary.maxRequestableAmount == null) return 0;
      const rawMax = props.summary.maxRequestableAmount;

      if (typeof rawMax === 'number') return rawMax;

      const maxAmountText = String(rawMax).replace('₺', '').trim();

      if (maxAmountText.includes(',')) {
        const normalized = maxAmountText.replace(/\./g, '').replace(',', '.');
        return parseFloat(normalized) || 0;
      }

      return parseFloat(maxAmountText) || 0;
    };

    const getMinAmount = () => {
      if (!props.summary || props.summary.minRequestAmount == null) return 0;
      return parseAmount(props.summary.minRequestAmount);
    };
    
    const validateRequestAmount = (requestAmountNum, maxAmount) => {
      if (requestAmountNum <= 0) {
        requestAmountErrorText.value = 'Lütfen geçerli bir tutar giriniz.';
        return true;
      }

      const minAmount = getMinAmount();
      const isMinRuleActive = minAmount > 0 && maxAmount >= minAmount;
      

      if (isMinRuleActive && requestAmountNum < minAmount) {
        requestAmountErrorText.value = `En az ${formatToTurkish(minAmount)} tutarında erken ödeme talep edebilirsiniz.`;
        return true;
      }

      if (requestAmountNum > maxAmount) {
        requestAmountErrorText.value = 'Talep edebileceğinizden daha yüksek bir tutar girdiniz.';
        return true;
      }

      requestAmountErrorText.value = '';
      return false;
    };

    const handleCalculateRequest = () => {
      const maxAmount = getMaxAmount();
      const requestAmountNum = parseAmount(requestAmount.value);

      const hasError = validateRequestAmount(requestAmountNum, maxAmount);
      const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
      if (hasError) {
        sendEarlySettlementCalculateDismissEvent({
          page_path: pagePath,
          button_name: "Hesapla",
          dismiss_reason: requestAmountErrorText.value,
        });
        return;
      }
      
      sendEarlySettlementCalculateEvent({
        page_path: pagePath,
        button_name: "Hesapla",
      });
      
      emit('calculate', requestAmountNum);
    };

    const normalizeAmount = (value) => {
      if (!value) return '';
      let strValue = String(value);
      strValue = strValue.replace(/₺/g, '');
      strValue = strValue.replace(/[^\d.,]/g, '');
      const commaIndex = strValue.indexOf(',');
      if (commaIndex !== -1) {
        const afterComma = strValue.substring(commaIndex + 1).replace(/[^\d]/g, '').substring(0, 2); // Maksimum 2 basamak
        const beforeComma = strValue.substring(0, commaIndex).replace(/[^\d]/g, '').substring(0, 9); // Maksimum 9 basamak
        const formattedBeforeComma = beforeComma.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        strValue = formattedBeforeComma + ',' + afterComma;
      } else {
        const numbersOnly = strValue.replace(/[^\d]/g, '').substring(0, 9); // Maksimum 9 basamak
        strValue = numbersOnly.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      }
      
      if (strValue) {
        return '₺' + strValue;
      }
      
      return '';
    };

    const formatToTurkish = (value) => {
      if (!value && value !== 0) return '';
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (isNaN(numValue)) return '';
      
      const parts = numValue.toString().split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1] || '';
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      if (decimalPart) {
        return `₺${formattedInteger},${decimalPart}`;
      }
      
      return `₺${formattedInteger}`;
    };

    watch(requestAmount, (newValue) => {
      if (isNormalizing.value) return;
      
      if (requestAmountErrorText.value) {
        clearError();
      }
      
      if (newValue !== null && newValue !== undefined && newValue !== '') {
        const normalized = normalizeAmount(newValue);
        if (newValue !== normalized) {
          isNormalizing.value = true;
          requestAmount.value = normalized;
          setTimeout(() => {
            isNormalizing.value = false;
          }, 0);
        }
      }
    });

    const handleKeyPress = (event) => {
      // Sadece sayı (0-9), nokta (.) ve virgül (,) karakterlerine izin ver
      const allowedChars = /[0-9.,]/;
      if (!allowedChars.test(event.key)) {
        event.preventDefault();
        return false;
      }
      
      // Eğer sayı giriliyorsa, maksimum basamak kontrolü yap
      if (event.target && /[0-9]/.test(event.key)) {
        const currentValue = event.target.value || requestAmount.value || '';
        const valueWithoutSymbols = currentValue.replace(/[₺.,]/g, '');
        const commaIndex = currentValue.indexOf(',');
        
        if (commaIndex !== -1) {
          // Virgül varsa, virgülden önce ve sonra ayrı kontrol et
          const beforeComma = currentValue.substring(0, commaIndex).replace(/[^\d]/g, '');
          const afterComma = currentValue.substring(commaIndex + 1).replace(/[^\d]/g, '');
          const cursorPos = event.target.selectionStart || 0;
          
          // Eğer virgülden sonra ise ve 2 basamak varsa engelle
          if (cursorPos > commaIndex && afterComma.length >= 2) {
            event.preventDefault();
            return false;
          }
          // Eğer virgülden önce ise ve 9 basamak varsa engelle
          if (cursorPos <= commaIndex && beforeComma.length >= 9) {
            event.preventDefault();
            return false;
          }
        } else {
          // Virgül yoksa, toplam 9 basamak kontrolü yap
          if (valueWithoutSymbols.length >= 9) {
            event.preventDefault();
            return false;
          }
        }
      }
    };

    const handlePaste = (event) => {
      event.preventDefault();
      return false;
    };

    let inputElement = null;
    onMounted(async () => {
      await nextTick();
      inputElement = amountInputRef.value?.$el?.querySelector('input') || 
                    document.getElementById('max-request-amount');
      if (inputElement) {
        inputElement.addEventListener('keypress', handleKeyPress);
        inputElement.addEventListener('paste', handlePaste);
      }
    });

    onUnmounted(() => {
      if (inputElement) {
        inputElement.removeEventListener('keypress', handleKeyPress);
        inputElement.removeEventListener('paste', handlePaste);
      }
    });

    const handleInput = () => {
      clearError();
      
      if (requestAmount.value !== null && requestAmount.value !== undefined && requestAmount.value !== '') {
        const normalized = normalizeAmount(requestAmount.value);
        if (requestAmount.value !== normalized) {
          isNormalizing.value = true;
          requestAmount.value = normalized;
          setTimeout(() => {
            isNormalizing.value = false;
          }, 0);
        }
      }
    };

    const clearError = () => {
      requestAmountErrorText.value = '';
    };

    watch(requestAmountErrorText, (newValue) => {
      emit('validation-error', !!newValue);
    });

    watch(() => props.isRequestCreated, (newValue) => {
      if (newValue) {
        requestAmount.value = null;
        requestAmountErrorText.value = '';
      }
    });

    const useAllBtnClick = () => {
      const maxAmount = getMaxAmount();
      if (!maxAmount) return;

      const rawMax = props.summary?.maxRequestableAmount;
      if (typeof rawMax === 'string' && rawMax.trim()) {
        requestAmount.value = normalizeAmount(rawMax);
      } else {
        requestAmount.value = formatToTurkish(maxAmount);
      }
      sendUseAllBalanceEvent({
        page_path: window.location.pathname,
        button_name: "Tamamını Kullan"
      });    
      requestAmountErrorText.value = '';
    };

    const isUseAllDisabled = computed(() => {
      return props.isRequestCreated || !isCreationAllowed.value || props.availabilityLoading || isBelowMinRequestAmount.value || isMaxRequestableAmountZero.value || !gradeAvailably.value;
    });

    const handleUseAllClick = () => {
      if (isUseAllDisabled.value) return;
      useAllBtnClick();
    };

    const isCreationAllowed = computed(() => {
      return !(props.availability?.creationAllowed ?? true);
    });

    return {
      requestAmount,
      handleCalculateRequest,
      useAllBtnClick,
      requestAmountErrorText,
      validateRequestAmount,
      clearError,
      isCreationAllowed,
      isUseAllDisabled,
      handleUseAllClick,
      amountInputRef,
      isRequestCreated: props.isRequestCreated,
      isBelowMinRequestAmount,
      isMaxRequestableAmountZero,
      gradeAvailably,
    };
  },
  props: {
    summary: {
      type: Object,
      default: () => ({}),
    },
    availability: {
      type: Object,
      default: () => ({}),
    },
    availabilityLoading: {
      type: Boolean,
      default: false,
    },
    isRequestCreated: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.early-settlement-left-content {
  display: flex;
  width: 648px;
  height: 490px;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  background: $colorN00;

  &.has-error {
    height: 510px;
  }

  &-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    &-text {
      align-self: stretch;
      color: $colorN80;
      font-size: 20px;
      font-weight: 700;
      line-height: 26px;
      letter-spacing: -0.2px;
    }
  }
    &-description {
      color: $colorN80;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    &-description-bold {
      font-weight: 600;
    }

  &-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    border-radius: 8px;
    min-height: fit-content; // Error text gösterildiğinde box genişleyebilsin

    &-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      margin-bottom: -8px;

      .item {
        display: flex;
        padding: 8px 8px 8px 0;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        border-bottom: 0.5px solid $colorN40;

        &:last-child {
          border-bottom: none;
          margin-bottom: 8px;
        }

        .item-label {
          color: $colorN80;
          font-size: 16px;
          font-weight: 400;
          line-height: 20px;
          letter-spacing: -0.05px;
        }

        .tooltip-info-icon {
          cursor: pointer;
        }

        &-label-container,
        &-label-container-wide {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        &-label-container {
          width: 267px;
        }

        &-label-container-wide {
          width: 294.5px;
        }

        .item-value {
          color: $colorN80;
          text-align: right;
          font-size: 20px;
          font-weight: 700;
          line-height: 26px;
          letter-spacing: -0.2px;
        }
        &.total-amount .item-value {
          color: $colorM50;
          font-size: 24px;
          line-height: 30px;
        }
      }
    }

    .info-calculation {
      &-input {
        display: flex;
        width: 600px;
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;

        &-field {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          align-self: stretch;

          &-app-button {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1 0 0;
            width: 400px;
            border-radius: 6px;

            .error-text {
              font-size: 11px;
              font-weight: 400;
              line-height: 14px;
              color: $colorE50;
              margin-left: 12px;
              min-height: 0;
              margin-top: 0;
              height: 0;
              overflow: hidden;
              
              &.error-text-visible {
                min-height: 19px;
                margin-top: 4px;
                height: auto;
              }
            }
          }
          .calculate-btn.disabled:hover {
            border: none;
          }
        }
      }
    }

    &-additional-info {
      display: flex;
      width: 600px;
      align-items: flex-start;
      gap: 2px;

      &-description,
      &-description-bold {
        color: $colorN60;
        font-size: 12px;
        line-height: 18px;
      }

      &-description {
        font-weight: 400;
      }

      &-description-bold {
        font-weight: 600;
      }
    }
  }

  /* input için number okları kaldırıldı */
  :deep(#max-request-amount[type="number"]) {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  :deep(#max-request-amount::-webkit-outer-spin-button),
  :deep(#max-request-amount::-webkit-inner-spin-button) {
    -webkit-appearance: none;
    margin: 0;
  }
}

.use-all-btn {
  color: $colorM50;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.05px;
  line-height: 18px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &.use-all-btn--disabled {
    color: $colorN40;
    cursor: default;
  }
}
</style>
